import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Award, TrendingUp, DollarSign, Target, UserPlus, Plus, Edit, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { collection, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import {
  fetchConferences,
  fetchAttributionData,
  buildConferenceReport,
  formatMoney,
} from '../utils/conferenceAttribution';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop } from '../components/crm/reportCards';
import {
  GLASS_PALETTE,
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_TH,
  GLASS_ROW,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GLASS_SECONDARY_BUTTON,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassChip,
  GlassBadge,
  GlassAccentButton,
  GlassSearch,
  GlassEmpty,
  GlassDonut,
  glassChartTheme,
} from '../components/glass/GlassUi';

// Compact axis labels: $12k, $1.2M
const compactMoney = (v) => {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1000000) return `$${Math.round(n / 100000) / 10}M`;
  if (abs >= 1000) return `$${Math.round(n / 100) / 10}k`;
  return `$${n}`;
};

// GlassTooltip look, plus a value formatter and the untruncated conference name
const ConferenceTooltip = ({ active, payload, label, format }) => {
  if (!active || !payload?.length) return null;
  const title = payload[0]?.payload?.fullName || label;
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-2 text-xs shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#14110f]/90">
      {title !== undefined && title !== '' && <div className="mb-1 text-gray-500 dark:text-white/[0.45]">{title}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color || p.stroke || p.fill }} />
          <span className="text-gray-700 dark:text-white/75">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold text-gray-900 tabular-nums dark:text-white">{format ? format(p.value, p.dataKey) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

const typeTone = (type) => (type === 'Speaker' ? 'lavender' : type === 'Sponsor' ? 'blue' : 'neutral');

const ConferencesReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  // State management
  const [conferences, setConferences] = useState([]);
  const [attribution, setAttribution] = useState({ accounts: [], purchases: [] });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingConference, setEditingConference] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newConference, setNewConference] = useState({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    type: 'Attendee', // Attendee, Sponsor, Speaker
    cost: '',
    notes: ''
  });

  const loadConferences = async () => {
    const conferencesData = await fetchConferences();
    setConferences(conferencesData);
    return conferencesData;
  };

  // Conferences plus the accounts and purchases their performance is derived from
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [, attributionData] = await Promise.all([
          loadConferences(),
          fetchAttributionData(),
        ]);
        setAttribution(attributionData);
      } catch (error) {
        console.error('Error fetching conference data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Leads, clients, MRR and revenue all come from matching accounts — only cost is entered by hand
  const report = useMemo(() => buildConferenceReport({
    conferences,
    accounts: attribution.accounts,
    purchases: attribution.purchases,
  }), [conferences, attribution]);

  const { rows, unspecified, totals } = report;
  const totalCost = totals.cost;
  const totalLeads = totals.leads;
  const totalClients = totals.clients;
  const totalRevenue = totals.revenue;
  const roi = totals.roi === null ? null : totals.roi.toFixed(1);

  // Prepare chart data
  const chartData = rows.map(row => ({
    name: row.name.length > 15 ? row.name.substring(0, 15) + '...' : row.name,
    fullName: row.name,
    cost: row.cost,
    leads: row.leads,
    clients: row.clients,
    revenue: row.revenue,
    roi: row.roi === null ? 0 : row.roi,
    date: row.date ? row.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : 'N/A',
    type: row.type
  }));

  // Monthly aggregated data
  const monthlyData = rows.reduce((acc, row) => {
    if (!row.date) return acc;
    const monthKey = row.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthKey, cost: 0, leads: 0, clients: 0, revenue: 0, count: 0 };
    }
    acc[monthKey].cost += row.cost;
    acc[monthKey].leads += row.leads;
    acc[monthKey].clients += row.clients;
    acc[monthKey].revenue += row.revenue;
    acc[monthKey].count += 1;
    return acc;
  }, {});

  const monthlyChartData = Object.values(monthlyData).sort((a, b) => {
    const dateA = new Date(a.month + ' 01');
    const dateB = new Date(b.month + ' 01');
    return dateA - dateB;
  });

  // Conference type distribution
  const typeData = rows.reduce((acc, row) => {
    const type = row.type || 'Unknown';
    if (!acc[type]) {
      acc[type] = { name: type, value: 0, cost: 0, leads: 0, clients: 0 };
    }
    acc[type].value += 1;
    acc[type].cost += row.cost;
    acc[type].leads += row.leads;
    acc[type].clients += row.clients;
    return acc;
  }, {});

  const pieChartData = Object.values(typeData);

  // Colors for charts
  const COLORS = GLASS_PALETTE;

  // Original docs by id, so the table can reach fields the report rows don't carry (notes)
  const conferencesById = useMemo(
    () => new Map(conferences.map(conf => [conf.id, conf])),
    [conferences]
  );

  // Filter conferences based on search query
  const filteredConferences = rows.filter(row => {
    const query = searchQuery.toLowerCase();
    if (!query) return true;
    const notes = conferencesById.get(row.id)?.notes || '';
    return (
      row.name?.toLowerCase().includes(query) ||
      row.location?.toLowerCase().includes(query) ||
      row.type?.toLowerCase().includes(query) ||
      notes.toLowerCase().includes(query)
    );
  });

  // Handle form submission. Leads, clients and revenue are never written here —
  // they are derived from accounts. Cost is the only number we can't infer.
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const conferenceData = {
        ...newConference,
        cost: parseFloat(newConference.cost) || 0,
        startDate: new Date(newConference.startDate),
        endDate: newConference.endDate ? new Date(newConference.endDate) : null,
        // Keep date for backward compatibility
        date: new Date(newConference.startDate),
        updatedAt: serverTimestamp()
      };

      if (editingConference) {
        // Older records live in `conferences-reports`; edit the document we actually read
        await updateDoc(doc(db, editingConference.collectionName || 'conferences', editingConference.id), conferenceData);
      } else {
        await addDoc(collection(db, 'conferences'), { ...conferenceData, createdAt: serverTimestamp() });
      }
      await loadConferences();

      setShowAddModal(false);
      setEditingConference(null);
      setNewConference({
        name: '',
        startDate: '',
        endDate: '',
        location: '',
        type: 'Attendee',
        cost: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error saving conference:', error);
    }
  };

  // Handle delete
  const handleDelete = async (conference) => {
    if (window.confirm('Are you sure you want to delete this conference?')) {
      try {
        await deleteDoc(doc(db, conference.collectionName || 'conferences', conference.id));
        setConferences(prev => prev.filter(conf => conf.id !== conference.id));
      } catch (error) {
        console.error('Error deleting conference:', error);
      }
    }
  };

  // Handle edit
  const handleEdit = (conference) => {
    setEditingConference(conference);
    setNewConference({
      name: conference.name || '',
      startDate: conference.startDate ? conference.startDate.toISOString().split('T')[0] : '',
      endDate: conference.endDate ? conference.endDate.toISOString().split('T')[0] : '',
      location: conference.location || '',
      type: conference.type || 'Attendee',
      cost: conference.cost != null ? conference.cost.toString() : '',
      notes: conference.notes || ''
    });
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <GlassPageHeader backTo="/crm/reports" backTitle="Back to reports" title={<>Conferences<br />Reports</>} />
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      </GlassPage>
    );
  }

  const chart = glassChartTheme(darkMode);
  const axisTick = { fill: chart.tick, fontSize: 11 };
  const moneyFormat = (value) => `$${Number(value || 0).toLocaleString()}`;
  const countFormat = (value, key) => (key === 'cost' || key === 'revenue' ? moneyFormat(value) : value);
  const pctFormat = (value) => `${Number(value || 0).toFixed(1)}%`;
  const roiTone = roi === null ? 'muted' : roi >= 0 ? 'mint' : 'red';
  const typeTotal = pieChartData.reduce((sum, d) => sum + d.value, 0);
  const typeDonutData = pieChartData.map((entry, index) => ({ ...entry, color: COLORS[index % COLORS.length] }));
  const conversionData = chartData.map(item => ({
    ...item,
    conversionRate: item.leads > 0 ? (item.clients / item.leads * 100) : 0
  }));
  const maxLeads = rows.reduce((m, r) => Math.max(m, r.leads), 0) || 1;

  const stats = [
    { icon: DollarSign, value: formatMoney(totalCost), label: 'Total cost' },
    { icon: UserPlus, value: totalLeads, label: 'Total leads' },
    { icon: Users, value: totalClients, label: 'Clients acquired (% of leads)', badge: `${totals.conversionRate}%`, badgeTone: 'mint' },
    { icon: TrendingUp, value: formatMoney(totalRevenue), label: 'Total revenue' },
    { icon: Award, value: formatMoney(totals.mrr), label: 'MRR' },
    {
      icon: Target,
      value: roi === null ? '—' : `${roi}%`,
      label: totals.costPerClient !== null ? `ROI · ${formatMoney(totals.costPerClient)} / client` : 'ROI',
      valueClassName: GLASS_TEXT[roiTone],
    },
  ];

  const xAxisProps = {
    tick: axisTick,
    axisLine: false,
    tickLine: false,
    interval: 'preserveStartEnd',
    minTickGap: 8,
  };
  const yAxisProps = { tick: axisTick, axisLine: false, tickLine: false, width: 52 };

  const iconBtn = 'grid h-8 w-8 place-items-center rounded-full border border-black/10 text-gray-600 transition hover:border-[#F80707]/40 hover:bg-[#F80707]/[0.06] hover:text-[#F80707] dark:border-white/10 dark:text-white/60 dark:hover:border-[#F80707]/50 dark:hover:bg-[#F80707]/10 dark:hover:text-[#FF4D40]';
  const labelCls = 'mb-1 block text-sm font-medium text-gray-600 dark:text-white/60';

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        backTo="/crm/reports"
        backTitle="Back to reports"
        title={<>Conferences<br />Reports</>}
        stats={stats}
        controls={(
          <GlassAccentButton onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" />
            Add Conference
          </GlassAccentButton>
        )}
      />

      {/* Metrics — everything except cost is derived from accounts */}
      <p className="-mt-4 mb-4 text-xs text-gray-500 dark:text-white/[0.45]">
        Leads, clients, MRR and revenue are pulled live from accounts whose lead source matches a conference. Only cost is entered by hand.
      </p>

      {unspecified.leads > 0 && (
        <div className="glass-rise mb-4 rounded-2xl border border-[#FFB547]/40 bg-[#FFB547]/10 p-4 text-sm text-[#94600A] dark:border-[#FFB547]/30 dark:bg-[#FFB547]/[0.08] dark:text-[#FFB547]">
          {unspecified.leads} lead{unspecified.leads === 1 ? '' : 's'} ({unspecified.clients} client{unspecified.clients === 1 ? '' : 's'}, {formatMoney(unspecified.revenue)}) are tagged “Conference” without a specific event, so they are not counted against any conference below. Set their lead source to the conference on the lead to include them.
        </div>
      )}

      {/* Charts Section */}
      <div className="mb-4">
        <h2 className="mb-4 text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-white/40">Conference Analytics</h2>

        {/* First Row - Monthly Trends and Conference Performance */}
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Monthly Trends */}
          <PinnableCard id="monthly-trends" title="Monthly Trends">
          <GlassCard className="p-5" delay={0}>
            <GlassCardHeader title="Monthly Trends" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.red} label="Cost" value={formatMoney(totalCost)} />
              <GlassChip color={GLASS_TONE.mint} label="Revenue" value={formatMoney(totalRevenue)} />
            </div>
            <div className="mt-4 h-64">
              {monthlyChartData.length === 0 ? (
                <GlassEmpty className="h-full">No conference data available</GlassEmpty>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="conf-cost-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GLASS_TONE.red} stopOpacity={0.32} />
                        <stop offset="100%" stopColor={GLASS_TONE.red} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="conf-revenue-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GLASS_TONE.mint} stopOpacity={0.38} />
                        <stop offset="100%" stopColor={GLASS_TONE.mint} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="month" {...xAxisProps} />
                    <YAxis {...yAxisProps} tickFormatter={compactMoney} />
                    <Tooltip content={<ConferenceTooltip format={countFormat} />} cursor={{ stroke: chart.grid }} />
                    <Area type="monotone" dataKey="cost" name="Cost" stackId="1" stroke={GLASS_TONE.red} strokeWidth={2} fill="url(#conf-cost-fill)" animationDuration={700} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stackId="2" stroke={GLASS_TONE.mint} strokeWidth={2} fill="url(#conf-revenue-fill)" animationDuration={700} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>

          {/* Conference Performance */}
          <PinnableCard id="conference-performance" title="Conference Performance">
          <GlassCard className="p-5" delay={80}>
            <GlassCardHeader title="Conference Performance" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.blue} label="Leads" value={totalLeads} />
              <GlassChip color={GLASS_TONE.mint} label="Clients" value={totalClients} />
            </div>
            <div className="mt-4 h-64">
              {chartData.length === 0 ? (
                <GlassEmpty className="h-full">No conference data available</GlassEmpty>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="22%" barGap={4}>
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="name" {...xAxisProps} />
                    <YAxis {...yAxisProps} allowDecimals={false} />
                    <Tooltip content={<ConferenceTooltip format={countFormat} />} cursor={{ fill: chart.cursor }} />
                    <Bar dataKey="leads" name="Leads" fill={GLASS_TONE.blue} radius={[8, 8, 8, 8]} maxBarSize={28} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                    <Bar dataKey="clients" name="Clients" fill={GLASS_TONE.mint} radius={[8, 8, 8, 8]} maxBarSize={28} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>
        </div>

        {/* Second Row - ROI Analysis and Conference Types */}
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* ROI Analysis */}
          <PinnableCard id="roi-analysis" title="ROI Analysis">
          <GlassCard className="p-5" delay={140}>
            <GlassCardHeader title="ROI Analysis" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.amber} label="ROI" value={roi === null ? '—' : `${roi}%`} />
            </div>
            <div className="mt-4 h-64">
              {chartData.length === 0 ? (
                <GlassEmpty className="h-full">No conference data available</GlassEmpty>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="conf-roi-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GLASS_TONE.amber} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={GLASS_TONE.amber} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="name" {...xAxisProps} />
                    <YAxis {...yAxisProps} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<ConferenceTooltip format={pctFormat} />} cursor={{ stroke: chart.grid }} />
                    <Area
                      type="monotone"
                      dataKey="roi"
                      name="ROI"
                      stroke={GLASS_TONE.amber}
                      strokeWidth={3}
                      fill="url(#conf-roi-fill)"
                      dot={{ fill: GLASS_TONE.amber, stroke: GLASS_TONE.amber, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 5 }}
                      animationDuration={700}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>

          {/* Conference Types Distribution */}
          <PinnableCard id="conference-types" title="Conference Types">
          <GlassCard className="p-5" delay={200}>
            <GlassCardHeader title="Conference Types" />
            {typeDonutData.length === 0 ? (
              <GlassEmpty className="h-64">No conference data available</GlassEmpty>
            ) : (
              <>
                <div className="my-4 h-52">
                  <GlassDonut data={typeDonutData} dataKey="value" nameKey="name" inner={80} outer={92} darkMode={darkMode} unit="conferences">
                    <div className="text-3xl font-medium text-gray-900 tabular-nums dark:text-white">{typeTotal}</div>
                    <div className="text-xs text-gray-500 dark:text-white/[0.45]">conferences</div>
                  </GlassDonut>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {typeDonutData.map((entry) => (
                    <GlassChip
                      key={entry.name}
                      color={entry.color}
                      label={entry.name}
                      value={`${typeTotal ? ((entry.value / typeTotal) * 100).toFixed(0) : 0}%`}
                    />
                  ))}
                </div>
              </>
            )}
          </GlassCard>
          </PinnableCard>
        </div>

        {/* Third Row - Cost vs Revenue and Lead Conversion */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Cost vs Revenue */}
          <PinnableCard id="cost-vs-revenue" title="Cost vs Revenue">
          <GlassCard className="p-5" delay={260}>
            <GlassCardHeader title="Cost vs Revenue" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.red} label="Cost" value={formatMoney(totalCost)} />
              <GlassChip color={GLASS_TONE.mint} label="Revenue" value={formatMoney(totalRevenue)} />
            </div>
            <div className="mt-4 h-64">
              {chartData.length === 0 ? (
                <GlassEmpty className="h-full">No conference data available</GlassEmpty>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="22%" barGap={4}>
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="name" {...xAxisProps} />
                    <YAxis {...yAxisProps} tickFormatter={compactMoney} />
                    <Tooltip content={<ConferenceTooltip format={moneyFormat} />} cursor={{ fill: chart.cursor }} />
                    <Bar dataKey="cost" name="Cost" fill={GLASS_TONE.red} radius={[8, 8, 8, 8]} maxBarSize={28} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                    <Bar dataKey="revenue" name="Revenue" fill={GLASS_TONE.mint} radius={[8, 8, 8, 8]} maxBarSize={28} background={{ fill: chart.track, radius: 8 }} animationDuration={700} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>

          {/* Lead Conversion Rate */}
          <PinnableCard id="lead-conversion-rate" title="Lead Conversion Rate">
          <GlassCard className="p-5" delay={320}>
            <GlassCardHeader title="Lead Conversion Rate" />
            <div className="mt-4 flex flex-wrap gap-2">
              <GlassChip color={GLASS_TONE.lavender} label="Conversion Rate" value={`${totals.conversionRate}%`} />
            </div>
            <div className="mt-4 h-64">
              {conversionData.length === 0 ? (
                <GlassEmpty className="h-full">No conference data available</GlassEmpty>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={conversionData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }} barCategoryGap="22%">
                    <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                    <XAxis dataKey="name" {...xAxisProps} />
                    <YAxis {...yAxisProps} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<ConferenceTooltip format={pctFormat} />} cursor={{ fill: chart.cursor }} />
                    <Bar dataKey="conversionRate" name="Conversion Rate" fill={GLASS_TONE.lavender} radius={[10, 10, 10, 10]} maxBarSize={36} background={{ fill: chart.track, radius: 10 }} animationDuration={700} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </GlassCard>
          </PinnableCard>
        </div>
      </div>

      {/* Search and Conferences Table */}
      <PinnableCard id="conferences-table" title="Conferences" size="wide">
      <GlassCard className="p-5" delay={380}>
        <GlassCardHeader
          title="Conferences"
          right={(
            <GlassSearch
              placeholder="Search conferences..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-56 sm:w-72"
            />
          )}
        />
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={GLASS_TH}>Conference</th>
                <th className={GLASS_TH}>Date Range</th>
                <th className={GLASS_TH}>Type</th>
                <th className={GLASS_TH}>Cost</th>
                <th className={`${GLASS_TH} w-48`}>Leads</th>
                <th className={GLASS_TH}>Clients</th>
                <th className={GLASS_TH}>MRR</th>
                <th className={GLASS_TH}>Revenue</th>
                <th className={GLASS_TH}>ROI</th>
                <th className={GLASS_TH}>Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-800 dark:text-white/[0.85]">
              {filteredConferences.map((row) => {
                const conference = conferencesById.get(row.id) || row;
                // Only documents in `conferences` have a detail page
                const hasDetailPage = row.collectionName === 'conferences';
                return (
                <tr
                  key={row.id}
                  onClick={() => hasDetailPage && navigate(`/crm/conferences-reports/${row.id}`)}
                  className={`${GLASS_ROW} ${hasDetailPage ? 'cursor-pointer' : ''}`}
                >
                  <td className="whitespace-nowrap py-3 pr-4">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {row.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-white/40">
                      {row.location}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    {row.date ? row.date.toLocaleDateString() : 'N/A'}
                    {row.endDate && row.endDate.getTime() !== row.date?.getTime() && (
                      <div className="text-xs text-gray-500 dark:text-white/40">
                        to {row.endDate.toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    <GlassBadge tone={typeTone(row.type)}>{row.type || 'Attendee'}</GlassBadge>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                    {formatMoney(row.cost)}
                    {row.costPerLead !== null && (
                      <div className="text-xs text-gray-500 dark:text-white/40">{formatMoney(row.costPerLead)} / lead</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <span className="w-6 font-semibold tabular-nums">{row.leads}</span>
                      <div className="h-1.5 flex-1 rounded-full bg-black/[0.06] dark:bg-white/[0.07]">
                        <div className="h-1.5 rounded-full" style={{ width: `${(row.leads / maxLeads) * 100}%`, backgroundColor: GLASS_TONE.blue }} />
                      </div>
                    </div>
                    {row.recordedLeads > 0 && row.recordedLeads !== row.leads && (
                      <div className="mt-1 text-xs text-gray-500 dark:text-white/40">{row.recordedLeads} recorded</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4">
                    <span className={`font-semibold ${GLASS_TEXT.mint}`}>{row.clients}</span>
                    {row.recordedClients > 0 && row.recordedClients !== row.clients && (
                      <div className="text-xs text-gray-500 dark:text-white/40">{row.recordedClients} recorded</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                    {formatMoney(row.mrr)}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-4 tabular-nums">
                    {formatMoney(row.revenue)}
                    {row.recordedRevenue > 0 && Math.round(row.recordedRevenue) !== Math.round(row.revenue) && (
                      <div className="text-xs text-gray-500 dark:text-white/40">{formatMoney(row.recordedRevenue)} recorded</div>
                    )}
                  </td>
                  <td className={`whitespace-nowrap py-3 pr-4 font-semibold ${GLASS_TEXT[row.roi === null ? 'muted' : row.roi >= 0 ? 'mint' : 'red']}`}>
                    {row.roi === null ? '—' : `${row.roi}%`}
                  </td>
                  <td className="whitespace-nowrap py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(conference);
                        }}
                        className={iconBtn}
                        aria-label="Edit conference"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(conference);
                        }}
                        className={iconBtn}
                        aria-label="Delete conference"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </GlassCard>
      </PinnableCard>

      {/* Add/Edit Modal — kept outside the glass cards so backdrop-filter can't trap it */}
      {showAddModal && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <h3 className="mb-5 flex items-center gap-2.5 text-lg font-medium">
              <span className="h-4 w-1 rounded-full bg-[#F80707]" />
              {editingConference ? 'Edit Conference' : 'Add Conference'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className={labelCls}>
                  Conference Name
                </label>
                <input
                  type="text"
                  value={newConference.name}
                  onChange={(e) => setNewConference({...newConference, name: e.target.value})}
                  className={GLASS_FIELD}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={newConference.startDate}
                    onChange={(e) => setNewConference({...newConference, startDate: e.target.value})}
                    className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
                    required
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={newConference.endDate}
                    onChange={(e) => setNewConference({...newConference, endDate: e.target.value})}
                    className={`${GLASS_FIELD} dark:[color-scheme:dark]`}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>
                  Location
                </label>
                <input
                  type="text"
                  value={newConference.location}
                  onChange={(e) => setNewConference({...newConference, location: e.target.value})}
                  className={GLASS_FIELD}
                  required
                />
              </div>

              <div>
                <label className={labelCls}>
                  Type
                </label>
                <select
                  value={newConference.type}
                  onChange={(e) => setNewConference({...newConference, type: e.target.value})}
                  className={`${GLASS_FIELD} dark:[&>option]:bg-[#14110f]`}
                >
                  <option value="Attendee">Attendee</option>
                  <option value="Sponsor">Sponsor</option>
                  <option value="Speaker">Speaker</option>
                </select>
              </div>

              <div>
                <label className={labelCls}>
                  Cost ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={newConference.cost}
                  onChange={(e) => setNewConference({...newConference, cost: e.target.value})}
                  className={GLASS_FIELD}
                />
                <p className="mt-2 text-xs text-gray-500 dark:text-white/[0.45]">
                  Leads, clients, MRR and revenue are calculated from accounts whose lead source is this conference — set the lead source on the lead and it shows up here automatically.
                </p>
              </div>

              <div>
                <label className={labelCls}>
                  Notes
                </label>
                <textarea
                  value={newConference.notes}
                  onChange={(e) => setNewConference({...newConference, notes: e.target.value})}
                  rows={3}
                  className={GLASS_FIELD}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingConference(null);
                    setNewConference({
                      name: '',
                      startDate: '',
                      endDate: '',
                      location: '',
                      type: 'Attendee',
                      cost: '',
                      notes: ''
                    });
                  }}
                  className={GLASS_SECONDARY_BUTTON}
                >
                  Cancel
                </button>
                <GlassAccentButton type="submit">
                  {editingConference ? 'Update' : 'Add'} Conference
                </GlassAccentButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default ConferencesReports;
